import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Navbar } from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { 
  Wallet, 
  ArrowUpCircle, 
  Send, 
  ArrowDownCircle, 
  ShoppingCart,
  Shield,
  Zap,
  Globe,
  ChevronRight,
  CreditCard,
  Smartphone,
  Heart
} from 'lucide-react';

export default function LandingPage() {
  const { t } = useLanguage();
  const { user } = useAuth();

  const features = [
    {
      icon: ArrowUpCircle,
      title: t('landing.feature_topup'),
      description: t('landing.feature_topup_desc'),
      color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
    },
    {
      icon: Send,
      title: t('landing.feature_transfer'),
      description: t('landing.feature_transfer_desc'),
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
    },
    {
      icon: ArrowDownCircle,
      title: t('landing.feature_withdraw'),
      description: t('landing.feature_withdraw_desc'),
      color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
    },
    {
      icon: ShoppingCart,
      title: t('landing.feature_api'),
      description: t('landing.feature_api_desc'),
      color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400'
    }
  ];

  const stats = [
    { value: '100K+', label: t('landing.stat_users') },
    { value: '50M$+', label: t('landing.stat_processed') },
    { value: '99.9%', label: t('landing.stat_uptime') },
    { value: '24/7', label: t('landing.stat_support') }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Shield className="h-4 w-4" />
                {t('landing.secure_fast')}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                {t('landing.hero_title')}
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-lg">
                {t('landing.hero_subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to={user ? '/dashboard' : '/register'}>
                  <Button size="lg" className="rounded-full px-8 shadow-lg hover:shadow-xl transition-all" data-testid="hero-cta">
                    {t('landing.get_started')}
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="#features">
                  <Button size="lg" variant="outline" className="rounded-full px-8" data-testid="learn-more-btn">
                    {t('landing.learn_more')}
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative animate-fade-in-up stagger-2 hidden lg:block">
              <div className="relative z-10">
                <Card className="bg-card shadow-2xl border-0 overflow-hidden">
                  <div className="bg-gradient-to-br from-[#003DA5] via-[#0052CC] to-[#0066CC] p-8 text-white">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <Wallet className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="text-white/80 text-sm">{t('landing.total_balance')}</p>
                        <p className="text-2xl font-bold">2 450,00$ CAD</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 rounded-xl bg-white/10">
                        <ArrowUpCircle className="h-5 w-5 mx-auto mb-1" />
                        <span className="text-xs">{t('landing.top_up')}</span>
                      </div>
                      <div className="text-center p-3 rounded-xl bg-white/10">
                        <Send className="h-5 w-5 mx-auto mb-1" />
                        <span className="text-xs">{t('landing.send')}</span>
                      </div>
                      <div className="text-center p-3 rounded-xl bg-white/10">
                        <ArrowDownCircle className="h-5 w-5 mx-auto mb-1" />
                        <span className="text-xs">{t('landing.withdraw')}</span>
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-sm font-medium mb-4">{t('landing.recent_activity')}</p>
                    <div className="space-y-3">
                      {[
                        { name: t('landing.topup_stripe'), amount: '+100,00$', type: 'in' },
                        { name: `${t('landing.transfer_to')} Jane D.`, amount: '-50,00$', type: 'out' },
                        { name: `${t('landing.received_from')} Mike`, amount: '+25,00$', type: 'in' }
                      ].map((tx, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                          <span className="text-sm">{tx.name}</span>
                          <span className={`text-sm font-medium ${tx.type === 'in' ? 'text-green-600' : 'text-red-500'}`}>
                            {tx.amount}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
              <div className="absolute -top-4 -right-4 h-72 w-72 bg-accent/30 rounded-full blur-3xl" />
              <div className="absolute -bottom-4 -left-4 h-48 w-48 bg-primary/20 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 border-y border-border/50 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center animate-fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
                <p className="text-3xl sm:text-4xl font-bold text-primary">{stat.value}</p>
                <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">{t('landing.features_title')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('landing.features_subtitle')}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <Card 
                key={i} 
                className="border-border/50 hover:shadow-lg transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${i * 0.1}s` }}
                data-testid={`feature-card-${i}`}
              >
                <CardContent className="p-6">
                  <div className={`h-12 w-12 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="flex items-start gap-4 animate-fade-in-up">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-2">{t('landing.trust_security')}</h3>
                <p className="text-sm text-muted-foreground">{t('landing.trust_security_desc')}</p>
              </div>
            </div>
            <div className="flex items-start gap-4 animate-fade-in-up stagger-2">
              <div className="h-12 w-12 rounded-xl bg-accent/20 flex items-center justify-center flex-shrink-0">
                <Zap className="h-6 w-6 text-accent-foreground" />
              </div>
              <div>
                <h3 className="font-semibold mb-2">{t('landing.trust_instant')}</h3>
                <p className="text-sm text-muted-foreground">{t('landing.trust_instant_desc')}</p>
              </div>
            </div>
            <div className="flex items-start gap-4 animate-fade-in-up stagger-3">
              <div className="h-12 w-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                <Globe className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold mb-2">{t('landing.trust_bilingual')}</h3>
                <p className="text-sm text-muted-foreground">{t('landing.trust_bilingual_desc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Users In Action Section - NEW */}
      <section className="py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">{t('landing.users_title')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('landing.users_subtitle')}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Online Shopping */}
            <div className="group relative animate-fade-in-up">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1758519291448-f768beb8912d?w=600&h=400&fit=crop" 
                  alt={t('landing.user_online')}
                  className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                      <CreditCard className="h-5 w-5" />
                    </div>
                    <h3 className="text-xl font-bold">{t('landing.user_online')}</h3>
                  </div>
                  <p className="text-white/80 text-sm">{t('landing.user_online_desc')}</p>
                </div>
              </div>
            </div>

            {/* In-Store Payments */}
            <div className="group relative animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1637021536331-17abe5429592?w=600&h=400&fit=crop" 
                  alt={t('landing.user_store')}
                  className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                      <Smartphone className="h-5 w-5" />
                    </div>
                    <h3 className="text-xl font-bold">{t('landing.user_store')}</h3>
                  </div>
                  <p className="text-white/80 text-sm">{t('landing.user_store_desc')}</p>
                </div>
              </div>
            </div>

            {/* Family Transfers */}
            <div className="group relative animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1758598737709-d32d6094a6e4?w=600&h=400&fit=crop" 
                  alt={t('landing.user_family')}
                  className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                      <Heart className="h-5 w-5" />
                    </div>
                    <h3 className="text-xl font-bold">{t('landing.user_family')}</h3>
                  </div>
                  <p className="text-white/80 text-sm">{t('landing.user_family_desc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-[#003DA5] via-[#0052CC] to-[#0066CC] text-white overflow-hidden">
            <CardContent className="p-8 sm:p-12 text-center relative">
              <div className="absolute top-0 right-0 h-64 w-64 bg-white/10 rounded-full blur-3xl" />
              <div className="relative z-10">
                <h2 className="text-3xl sm:text-4xl font-bold mb-4">{t('landing.cta_title')}</h2>
                <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
                  {t('landing.cta_subtitle')}
                </p>
                <Link to={user ? '/dashboard' : '/register'}>
                  <Button size="lg" variant="secondary" className="rounded-full px-8 shadow-lg" data-testid="cta-btn">
                    {t('landing.cta_button')}
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
